import {
	SectorTypes,
	AttachmentType,
	DeviceListLevel,
	UserRoles,
	KeyCodes,
	Gender,
	AppointmentStatus,
	AndroidVersions,
	DeviceFamilyTypes,
	ParticipantState,
	MediaInputs,
	MediaPermissions,
	TreeHierarchyType,
	CompanyRoleObjects,
	CompanyRoles,
	UserTypes,
	RequestType,
	CallRequestType,
	IframeIntregrationsIds,
} from 'constants/enums.js';
import queryString from 'query-string';
import { RoomTypes } from 'constants/visitEnums.js';
import { getStartPoint, getEndPoint } from 'services/aiDetections/fallDetectionConstants.js';
import { healthCareCdnUrl } from 'constants/global-variables.js';
import translate from 'i18n-translations/translate.jsx';
import moment from 'moment';
import { getCompanyId, getUserRole } from 'infrastructure/auth.js';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import {
	getHospitalBreadCrumb,
	getDepartmentBreadCrumb,
	getFloorBreadCrumb,
	getHospitalFloorsBreadCrumb,
	getBreadCrumbBasedHierarchy,
} from 'infrastructure/helpers/breadcrumbHelper.js';
import LightTheme from 'calls/styles/LightTheme.js';
import DarkTheme from 'calls/styles/DarkTheme.js';
import {
	IntegrationTypesSettings,
	TeamConfigurationIntegrationTypes,
	configurableMonitoringMenu,
	UserSettings,
	getConfig,
	CompanySettings,
	GeneralAndMenuSettings,
	configurableAISettings,
} from 'constants/configurationEnums.js';
import { decode } from 'html-entities';
import { VirtualBackgroundTypes } from 'calls/views/VirtualBackgroundProvider.jsx';
import { getBackgroundImages } from 'api/configurations.js';
import { changeLocalParticipantBackground } from 'calls/helpers/index.js';
import _ from 'lodash';

const treeImg = () => `${healthCareCdnUrl}treeview/`;

const regexForDecimalNumbers = new RegExp('^[0-9]{0,3}([,.][0-9]{0,2})?$');

export const isSessionEhr = () => {
	return sessionStorage.getItem('ehr.sid') !== null;
};

const hasRefTokenInQuery = () => {
	const { search, pathname } = window.location;
	const query = queryString.parse(search);
	const isAmwell = +query.iid === IframeIntregrationsIds.AMWELL;

	if (
		(query.refToken && (pathname.startsWith('/virtual-session/') || pathname.startsWith('/virtual-care-session/'))) ||
		isAmwell
	) {
		return query.refToken.toString();
	}
	return null;
};

let chosenStorage;

export const getStorage = () => {
	if (chosenStorage) {
		return chosenStorage;
	}
	let _storage = window !== window.parent ? sessionStorage : localStorage;

	const hasRefToken = hasRefTokenInQuery() || sessionStorage.getItem('ref_token');

	if (hasRefToken || isSessionEhr()) {
		chosenStorage = sessionStorage;
	} else {
		chosenStorage = _storage;
	}

	return chosenStorage;
};

export const isUUIDEmpty = uuid => {
	const emptyUUID = '00000000-0000-0000-0000-000000000000';
	if (uuid === null || uuid === undefined || uuid === emptyUUID) {
		return true;
	}
	return false;
};

export const getHierarchyByHelloDeviceId = (arr, id) => {
	return arr.reduce((accumulator, item) => {
		if (accumulator) {
			return accumulator;
		}

		if (+item.helloDeviceId === +id) {
			return getHierarchyFromBreadcrumb(item.breadcrumb);
		}
		if (item.subOptions) {
			return getHierarchyByHelloDeviceId(item.subOptions, id);
		}
	}, null);
};

export const getHierarchyFromBreadcrumb = breadcrumb => {
	let hierarchy = '';
	breadcrumb.forEach(({ name }, index) => {
		if (index > 2) {
			hierarchy = `${hierarchy} - ${name}`;
		} else {
			hierarchy = `${hierarchy}, ${name}`;
		}
	});
	return hierarchy.substring(2);
};
export const setCurrentHealthSystemIfNotExists = async healthSystem => {
	if (!getStorage().getItem('currentHealthSystemInfo')) {
		await setCurrentHealthSystem(healthSystem);
	}
};

export const setCurrentHealthSystem = async healthSystem => {
	const currentHealthSystemInfo = {
		currentHealthSystemId: healthSystem.id,
		currentHealthSystemName: healthSystem.name,
		currentRegionId: healthSystem.regions[0].id,
	};
	getStorage().setItem('currentHealthSystemInfo', JSON.stringify(currentHealthSystemInfo));
};

// Returns String: Hospital, Department, Floor, Room
export const getRoomLocation = breadcrumb => {
	let result = '';
	breadcrumb.forEach((item, index) => {
		result += breadcrumb.length - 1 !== index ? `${item.name}, ` : item.name;
	});
	return result;
};

export const findSectorById = (arr, id) => {
	if (!arr) {
		return null;
	}
	return arr.reduce((accumulator, item) => {
		if (accumulator) {
			return accumulator;
		}
		// Comparing whether the id given as argument is equal to any of the items declared in the array.
		if ([item.hospitalId, item.departmentId, item.floorId, item.roomId, item.id, item.helloDeviceId].includes(id)) {
			return item;
		}
		if (item.subOptions) {
			return findSectorById(item.subOptions, id);
		}
	}, null);
};

export const checkIfMediaDevicesPlugged = async () => {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices();
		const pluggedDevices = {
			microphone: false,
			camera: false,
		};

		devices.forEach(device => {
			if (device.kind === MediaInputs.AUDIO_INPUT) {
				pluggedDevices.microphone = true;
			}

			if (device.kind === MediaInputs.VIDEO_INPUT) {
				pluggedDevices.camera = true;
			}
		});
		return pluggedDevices;
	} catch (error) {
		return { error };
	}
};

export const askForPermission = async permissionFor => {
	try {
		const response = await navigator.mediaDevices.getUserMedia(permissionFor);
		return { permission: MediaPermissions.GRANTED, response };
	} catch (error) {
		console.error(error);
		return { error };
	}
};

export const checkForPermission = async permissionFor => {
	try {
		return await navigator.permissions.query({ name: permissionFor });
	} catch (error) {
		return { error };
	}
};

export function isMacintosh() {
	return navigator.platform.includes('Mac');
}

export const decodeHtml = txt => decode(txt && txt.replace(/amp;/g, ''));

export const getParentSector = (arr, element) => {
	let parent = {};
	if (!element) {
		return parent;
	}
	if (element.type === SectorTypes.ROOM) {
		parent = findSectorById(arr, element.floorId);
	}
	if (element.type === SectorTypes.FLOOR) {
		parent = findSectorById(arr, element.departmentId);
	}
	if (element.type === SectorTypes.DEPARTMENT) {
		parent = findSectorById(arr, element.hospitalId);
	}
	return parent;
};

export const checkIfSectorNameExists = (currentSector, tree, sectorName) => {
	let sameLevelSectors;
	if (currentSector.type === SectorTypes.HOSPITAL) {
		sameLevelSectors = tree.filter(s => s.isNewOption === undefined);
		const hospitalExists = Object.entries(sameLevelSectors.length && sameLevelSectors[0]).length > 0;
		if (!hospitalExists) {
			return true;
		}
	} else {
		sameLevelSectors = getParentSector(tree, currentSector)?.subOptions?.filter(s => s.isNewOption === undefined);
	}
	return !sameLevelSectors?.some(sector => sector.name.toLowerCase() === sectorName.toLowerCase());
};

export const getSectorPath = sector => {
	const result = {};
	if (!sector) {
		return result;
	}
	if (sector.type === SectorTypes.HOSPITAL) {
		result[sector.regionId] = {};
	}
	if (sector.type === SectorTypes.DEPARTMENT) {
		result[sector.regionId] = {};
		result[sector.regionId][sector.hospitalId] = {};
	}
	if (sector.type === SectorTypes.FLOOR) {
		result[sector.regionId] = {};
		result[sector.regionId][sector.hospitalId] = {};
		result[sector.regionId][sector.hospitalId][sector.departmentId] = {};
	}
	if (sector.type === SectorTypes.ROOM) {
		result[sector.regionId] = {};
		result[sector.regionId][sector.hospitalId] = {};
		result[sector.regionId][sector.hospitalId][sector.departmentId] = {};
		result[sector.regionId][sector.hospitalId][sector.departmentId][sector.floorId] = {};
	}
	return result;
};

const getHospitalDetails = ({ hospital, title, defaultDepartmentId = null }) => ({
	name: hospital.name,
	icon: 'business',
	imgIcon: `${treeImg()}Hospital.svg`,
	activeImgIcon: `${treeImg()}Hospital-active.svg`,
	link: `${hospital.id}`,
	tooltip: [UserRoles.ADMIN, UserRoles.SUPER_USER].includes(getUserRole()) ? title : false,
	type: 'hospital',
	subOptions: [],
	hospitalId: hospital.id,
	breadcrumb: getHospitalBreadCrumb(hospital),
	defaultDepartmentId,
	timezone: hospital.timezone,
	pin: hospital.helloSettingsPIN,
});

const getDepartmentDetails = ({ hospital, department, title }) => ({
	name: department.name,
	icon: 'account_balance',
	imgIcon: `${treeImg()}Department.svg`,
	activeImgIcon: `${treeImg()}Department-active.svg`,
	link: `${hospital.id}/department/${department.id}`,
	tooltip: [UserRoles.ADMIN, UserRoles.SUPER_USER].includes(getUserRole()) ? title : false,
	type: 'department',
	subOptions: [],
	departmentId: department.id,
	hospitalId: hospital.id,
	breadcrumb: getDepartmentBreadCrumb(hospital, department),
	unitPhoneNumber: department.unitPhoneNumber,
	whiteboardTypeId: department?.whiteboardTypeId,
});

const getFloorDetails = ({ hospital, department, floor, hierarchyTypeId }) => ({
	name: floor.name,
	icon: 'money',
	imgIcon: `${treeImg()}Floors.svg`,
	activeImgIcon: `${treeImg()}Floors-active.svg`,
	link: `${hospital.id}/department/${department.id}/floor/${floor.id}`,
	tooltip: [UserRoles.ADMIN, UserRoles.SUPER_USER].includes(getUserRole()) ? 'addRoom' : false,
	type: 'floor',
	subOptions: [],
	floorId: floor.id,
	departmentId: department.id,
	hospitalId: hospital.id,
	breadcrumb: [TreeHierarchyType.DEFAULT_TREE, TreeHierarchyType.HOSPITAL_DEPT_FLOOR_ROOM].includes(hierarchyTypeId)
		? getFloorBreadCrumb(hospital, department, floor)
		: getHospitalFloorsBreadCrumb(hospital, department, floor),
});

const getRoomDetails = ({ hospital, department, floor, room, roomIndex, hierarchyTypeId }) => ({
	name: room.name,
	icon: 'meeting_room',
	imgIcon: `${treeImg()}Room.svg`,
	activeImgIcon: `${treeImg()}Room-active.svg`,
	link: `${hospital.id}/department/${department.id}/floor/${floor.id}/room/${room.id}`,
	type: 'room',
	subOptions: [],
	roomId: room.id,
	roomIndex,
	floorId: floor.id,
	departmentId: department.id,
	hospitalId: hospital.id,
	roomType: room.type,
	tooltip: room.helloDeviceId ? 'addToMonitoringView' : null,
	isHelloDevice: room?.deviceFamily !== DeviceFamilyTypes.TABLET,
	helloDeviceId: room.helloDeviceId ? parseInt(room.helloDeviceId, 10) : room.helloDeviceId,
	breadcrumb: getBreadCrumbBasedHierarchy(hierarchyTypeId, hospital, department, floor, room),
	status: room.isOnline ? 'online' : 'offline',
	privacyMode: room.privacyMode,
	alexaServiceStatus: room.alexaServiceStatus,
	isMultiBed: room.isMultiBed,
});

export const buildTree = (healthSystem, isRoomsOnly = false) => {
	const treeData = [];
	if (isRoomsOnly) {
		healthSystem.hospitals.forEach((hospital, hospitalIndex) => {
			treeData.push({
				name: hospital.name,
				icon: 'business',
				imgIcon: `${treeImg()}Hospital.svg`,
				activeImgIcon: `${treeImg()}Hospital-active.svg`,
				link: `${hospital.id}`,
				type: 'hospital',
				subOptions: [],
				hospitalId: hospital.id,
				breadcrumb: getHospitalBreadCrumb(hospital),
				isRoomsOnly,
				timezone: hospital.timezone,
			});
			hospital.departments.forEach(department => {
				department.floors.forEach(floor => {
					floor.rooms.forEach((room, roomIndex) => {
						treeData[hospitalIndex].subOptions.push(
							getRoomDetails({
								hospital,
								department,
								floor,
								room,
								roomIndex,
								hierarchyTypeId: healthSystem.treeHierarchyTypeId,
							})
						);
					});
				});
			});
		});
	} else if (
		[TreeHierarchyType.DEFAULT_TREE, TreeHierarchyType.HOSPITAL_DEPT_FLOOR_ROOM].includes(healthSystem.treeHierarchyTypeId)
	) {
		healthSystem.hospitals.forEach((hospital, hospitalIndex) => {
			treeData.push(getHospitalDetails({ hospital, title: 'addDepartment' }));
			hospital.departments.forEach((department, departmentIndex) => {
				treeData[hospitalIndex].subOptions.push(getDepartmentDetails({ hospital, department, title: 'addFloor' }));
				department.floors.forEach((floor, floorIndex) => {
					treeData[hospitalIndex].subOptions[departmentIndex].subOptions.push(
						getFloorDetails({ hospital, department, floor, hierarchyTypeId: healthSystem.treeHierarchyTypeId })
					);
					floor.rooms.forEach((room, roomIndex) => {
						treeData[hospitalIndex].subOptions[departmentIndex].subOptions[floorIndex].subOptions.push(
							getRoomDetails({
								hospital,
								department,
								floor,
								room,
								roomIndex,
								hierarchyTypeId: healthSystem.treeHierarchyTypeId,
							})
						);
					});
				});
			});
		});
	} else if (healthSystem.treeHierarchyTypeId === TreeHierarchyType.HOSPITAL_DEPT_ROOM) {
		healthSystem.hospitals.forEach((hospital, hospitalIndex) => {
			treeData.push(getHospitalDetails({ hospital, title: 'addDepartment' }));
			hospital.departments.forEach((department, departmentIndex) => {
				treeData[hospitalIndex].subOptions.push(getDepartmentDetails({ hospital, department, title: 'addRoom' }));
				department.floors.forEach(floor => {
					floor.rooms.forEach((room, roomIndex) => {
						treeData[hospitalIndex].subOptions[departmentIndex].subOptions.push(
							getRoomDetails({
								hospital,
								department,
								floor,
								room,
								roomIndex,
								hierarchyTypeId: healthSystem.treeHierarchyTypeId,
							})
						);
					});
				});
			});
		});
	} else if (healthSystem.treeHierarchyTypeId === TreeHierarchyType.HOSPITAL_FLOOR_ROOM) {
		healthSystem.hospitals.forEach((hospital, hospitalIndex) => {
			treeData.push(getHospitalDetails({ hospital, title: 'addFloor' }));
			hospital.departments.forEach(department => {
				department.floors.forEach((floor, floorIndex) => {
					treeData[hospitalIndex].subOptions.push(
						getFloorDetails({ hospital, department, floor, hierarchyTypeId: healthSystem.treeHierarchyTypeId })
					);
					floor.rooms.forEach((room, roomIndex) => {
						treeData[hospitalIndex].subOptions[floorIndex].subOptions.push(
							getRoomDetails({
								hospital,
								department,
								floor,
								room,
								roomIndex,
								hierarchyTypeId: healthSystem.treeHierarchyTypeId,
							})
						);
					});
				});
			});
		});
	} else if (healthSystem.treeHierarchyTypeId === TreeHierarchyType.HOSPITAL_ROOM) {
		healthSystem.hospitals.forEach((hospital, hospitalIndex) => {
			treeData.push(
				getHospitalDetails({
					hospital,
					title: 'addRoom',
					defaultDepartmentId:
						hospital.departments !== null && hospital.departments.length === 1 ? hospital.departments[0].id : null,
				})
			);
			hospital.departments.forEach(department => {
				department.floors.forEach(floor => {
					floor.rooms.forEach((room, roomIndex) => {
						treeData[hospitalIndex].subOptions.push(
							getRoomDetails({
								hospital,
								department,
								floor,
								room,
								roomIndex,
								hierarchyTypeId: healthSystem.treeHierarchyTypeId,
							})
						);
					});
				});
			});
		});
	}
	return treeData;
};

export const areSameObject = (object1, object2) =>
	object1.objectId.toString() === object2.objectId.toString() && object1.objectTypeId === object2.objectTypeId;
export const isDocumentAttachment = attachmentTypeId => attachmentTypeId === AttachmentType.DOCUMENT;
export const isVideoAttachment = attachmentTypeId => attachmentTypeId === AttachmentType.VIDEO;
export const isPictureAttachment = attachmentTypeId => attachmentTypeId === AttachmentType.PICTURE;
export const getAttachmentIcon = fileName => {
	const fileExtension = fileName.split('.').pop().toLowerCase();
	switch (fileExtension) {
		case 'jpg':
		case 'jpeg':
		case 'png':
		case 'bmp':
			return `${healthCareCdnUrl}requests/image.svg`;
		case 'txt':
			return `${healthCareCdnUrl}requests/text.svg`;
		case 'doc':
		case 'docx':
			return `${healthCareCdnUrl}requests/doc.svg`;
		case 'xls':
		case 'xlsx':
			return `${healthCareCdnUrl}requests/xlsx.svg`;
		case 'pdf':
			return `${healthCareCdnUrl}requests/pdf.svg`;
		case 'ppt':
		case 'pptx':
			return `${healthCareCdnUrl}requests/pptx.svg`;
		case 'mpg':
		case 'mp4':
		case 'mkv':
		case '3gp':
		case 'm4v':
		case 'mov':
			return `${healthCareCdnUrl}requests/video.svg`;
		default:
			return 'invalidFileType';
	}
};

export const getGender = genderId => {
	switch (genderId) {
		case Gender.MALE:
			return { description: translate('male'), icon: `${healthCareCdnUrl}sign-up/male.svg` };
		case Gender.FEMALE:
			return { description: translate('female'), icon: `${healthCareCdnUrl}sign-up/female.svg` };
		case Gender.NON_BINARY:
			return { description: translate('nonBinary'), icon: `${healthCareCdnUrl}sign-up/non-binary.svg?v2` };
		default:
			return { description: '', icon: '' };
	}
};

export const removeSpecialCharacters = fileName => {
	return fileName.replace(/[^\w\s]/gi, '');
};
export const takeScreenshot = videoElement => {
	if (!videoElement) {
		return;
	}
	const canvas = document.createElement('canvas');
	canvas.width = window.innerWidth;
	canvas.height = window.innerHeight;
	const ctx = canvas.getContext('2d');
	ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
	const anchor = document.createElement('a');
	anchor.href = canvas.toDataURL('image/jpeg');
	anchor.download = `${+new Date()}${Math.random()}.jpeg`;
	anchor.hidden = true;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
};

export const getInitialsColor = letter => {
	const obj = {};
	if (!letter) {
		obj.color = `4772f3`;
		obj.backgroundColor = `d0e2fd`;
		return obj;
	}
	const letterToLowerCase = letter.toLowerCase();
	switch (letterToLowerCase) {
		case 'a':
		case 'n': {
			obj.color = `4772f3`;
			obj.backgroundColor = `d0e2fd`;
			break;
		}
		case 'b':
		case 'o': {
			obj.color = `e361c9`;
			obj.backgroundColor = `edd2fc`;
			break;
		}
		case 'c':
		case 'p': {
			obj.color = `eb605e`;
			obj.backgroundColor = `f9dcd2`;
			break;
		}
		case 'd':
		case 'q': {
			obj.color = `15a6ff`;
			obj.backgroundColor = `d2f0fc`;
			break;
		}
		case 'e':
		case 'r': {
			obj.color = `44ccb5`;
			obj.backgroundColor = `d2f7ec`;
			break;
		}
		case 'f':
		case 's': {
			obj.color = `dd995b`;
			obj.backgroundColor = `f7e4d2`;
			break;
		}
		case 'g':
		case 't': {
			obj.color = `e2619c`;
			obj.backgroundColor = `f9d2f8`;
			break;
		}
		case 'h':
		case 'u': {
			obj.color = `73c140`;
			obj.backgroundColor = `d2f7d6`;
			break;
		}
		case 'i':
		case 'v': {
			obj.color = `8a64dd`;
			obj.backgroundColor = `e2d2f9`;
			break;
		}
		case 'j':
		case 'w': {
			obj.color = `d3f4e8`;
			obj.backgroundColor = `41837a`;
			break;
		}
		case 'k':
		case 'x': {
			obj.color = `b94557`;
			obj.backgroundColor = `f4d3d3`;
			break;
		}
		case 'l':
		case 'y': {
			obj.color = `fcfaef`;
			obj.backgroundColor = `d2f7d6`;
			break;
		}
		case 'm':
		case 'z': {
			obj.color = `70a8b5`;
			obj.backgroundColor = `e8f5f9`;
			break;
		}
		default:
	}
	return obj;
};

export const getCoverElementDimensions = elementToCover => {
	let result = {};
	if (!elementToCover) {
		return result;
	}
	const { width } = elementToCover.getBoundingClientRect();
	result = { width, height: (width * 9) / 16 };
	return result;
};

export const reOrderRecentList = (conversations, items) => {
	if (conversations.length === 0) {
		return items;
	}
	const myItems = [];
	let foundItem = null;
	items.forEach(patient => {
		const foundConversation = conversations.find(conversation => {
			let result = null;
			if (conversation.typeId === 0 && patient.owner.id === Number(conversation.contact.objectId)) {
				result = conversation;
			}
			return result;
		});
		if (foundConversation) {
			foundItem = patient;
			foundItem.time = foundConversation.lastActivity.time;
			myItems.push(foundItem);
		} else {
			myItems.push(patient);
		}
	});
	myItems.sort((a, b) => b.time - a.time);
	return myItems;
};

export const convertSecondsToHoursFormat = (secondsString, showSeconds = false) => {
	if (!secondsString) {
		return 'N/A';
	}
	const seconds = parseInt(secondsString, 10);
	const hours = seconds / 3600;
	const hoursInteger = Math.floor(hours);
	const minutesInteger = Math.floor((hours - hoursInteger) * 60);
	const secondsInteger = `${String(seconds).substring(0, 2)}s`;
	return `${hoursInteger}h ${minutesInteger.toString().padStart(2, '0')}m ${showSeconds ? secondsInteger : ''}`;
};

export const convertHoursFormatToSeconds = date => {
	const hours = parseInt(date.substring(0, date.indexOf('h')), 10);
	const minutes = parseInt(date.substring(date.indexOf(' '), date.indexOf('m')), 10);
	return hours * 3600 + minutes * 60;
};

export const handleOnKeyDownNumeric = (event, allowPeriod = false) => {
	if (
		[
			KeyCodes.MINUS,
			KeyCodes.NUMPAD_PERIOD,
			KeyCodes.NUMPAD_ADD,
			KeyCodes.NUMPAD_SUBTRACT,
			!allowPeriod && KeyCodes.PERIOD,
			KeyCodes.PLUS,
			KeyCodes.LETTER_E,
		].includes(event.which)
	) {
		event.preventDefault();
	}
};

export const allowOnlyNumbersOrLetters = event => {
	const { value } = event.target;

	if (!event.key.match(/[a-zA-Z0-9,]/) || (event.key === ',' && value[value.length - 1] === ',')) {
		event.preventDefault();
	}
};

export const doNotAllowSpaceAsFirstCharacter = event => {
	if (!event.target.value && event.which === KeyCodes.SPACE) {
		event.preventDefault();
	}
};

export const handleOnKeyDownCellPhoneNumbers = event => {
	if (
		[
			KeyCodes.MINUS,
			KeyCodes.NUMPAD_PERIOD,
			KeyCodes.NUMPAD_ADD,
			KeyCodes.NUMPAD_SUBTRACT,
			KeyCodes.PERIOD,
			KeyCodes.LETTER_E,
			KeyCodes.PLUS,
		].includes(event.which) ||
		(event.which >= KeyCodes.LETTER_A && event.which <= KeyCodes.LETTER_Z)
	) {
		event.preventDefault();
	}
};

export const stringToCamelCase = str => {
	return str
		.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
			return index === 0 ? word.toLowerCase() : word.toUpperCase();
		})
		.replace(/\s+|\(|\)/g, '');
};

export const capitalizeFirstLetter = string => {
	if (typeof string !== 'string') return '';
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getPreferredLanguageLocale = () => {
	return getStorage().getItem('preferredLanguage');
};

export const setPreferredLanguageLocale = preferredLanguage => {
	getStorage().setItem('preferredLanguage', preferredLanguage);
};

export const setConversationMembersInfo = (members, conversationId) => {
	const conversationMembersInfo = getStorage().getItem('conversationMembersInfo');
	if (conversationMembersInfo) {
		const parsedConversationMembersInfo = JSON.parse(conversationMembersInfo);
		parsedConversationMembersInfo[conversationId] = members;
		getStorage().setItem('conversationMembersInfo', JSON.stringify(parsedConversationMembersInfo));
	} else {
		getStorage().setItem('conversationMembersInfo', JSON.stringify({ [conversationId]: members }));
	}
};

export const getConversationMembersInfo = conversationId => {
	const conversationInfo = JSON.parse(getStorage().getItem('conversationMembersInfo')) ?? {};
	return conversationInfo[conversationId] ?? [];
};

export const getPositionOfMainSkeleton = (skeletonArray, videoRef) => {
	let position = null;

	const x1 = videoRef.clientWidth * getStartPoint();
	const x2 = videoRef.clientWidth * getEndPoint();
	for (let index = 0; index < skeletonArray.length; index += 1) {
		let counter = 0;
		skeletonArray[index].keypoints.forEach(keypoint => {
			if (keypoint.position.x >= x1 && keypoint.position.x <= x2) {
				counter += 1;
			}
		});
		if (counter > skeletonArray[index].keypoints.length / 2) {
			position = index;
			break;
		}
	}
	return position;
};

export const adjustPositions = (skeletons, clientWidth, clientHeight) => {
	const newSkeletons = [...skeletons];
	newSkeletons.forEach(item => {
		item.keypoints.forEach(keyPoint => {
			const position = { ...keyPoint.position };
			position.x = keyPoint.position.x * clientWidth;
			position.y = keyPoint.position.y * clientHeight;
			// eslint-disable-next-line no-param-reassign
			keyPoint.position = position;
		});
	});
	return newSkeletons;
};

export const getBaseUrl = () => {
	if (window.location.pathname.startsWith('/health-system')) {
		return '/health-system';
	}
	if (window.location.pathname.startsWith('/monitoring')) {
		return '/monitoring';
	}
	if (window.location.pathname.startsWith('/visitors')) {
		return '/visitors';
	}
	return '';
};

export const findDeviceById = (arr, id) => {
	// eslint-disable-next-line consistent-return
	return arr.reduce((accumulator, item) => {
		if (accumulator) {
			return accumulator;
		}
		if (+item.helloDeviceId === +id) {
			return item;
		}
		if (item.subOptions) {
			return findDeviceById(item.subOptions, +id);
		}
	}, null);
};

export const findRoomOnHs = (hs, deviceId) => {
	let room;
	hs.hospitals.forEach(hos => {
		hos.departments.forEach(dep => {
			dep.floors.forEach(fl => {
				if (!room) {
					// eslint-disable-next-line eqeqeq
					room = fl.rooms.find(x => x.helloDeviceId == deviceId);
				}
			});
		});
	});
	return room;
};

export const skipDuplicatedObjects = (objects, key) => {
	return [...new Map(objects.map(obj => [obj[key], obj])).values()];
};

export const mapDevicesToPatients = (patients, devices) => {
	patients.forEach(patient => {
		// eslint-disable-next-line no-param-reassign
		patient.devices = devices.reduce((acc, device) => {
			if (device.userId === patient.userId && device.deviceInfo) {
				acc.push(device.deviceInfo);
			}
			return acc;
		}, []);
	});
	return patients;
};

export const isMobileOrTablet = () => {
	let isMobileOrTablet = false;

	(a => {
		if (
			/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
				a
			) ||
			/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
				a.substr(0, 4)
			)
		) {
			isMobileOrTablet = true;
		}
	})(navigator.userAgent || navigator.vendor || window.opera);

	return isMobileOrTablet;
};

export const isEmailValid = email => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const getFirstName = fullName => fullName.split(' ').slice(0, -1).join(' ');

export const getLastName = fullName => fullName.split(' ').slice(-1).join(' ');

export const getDeviceAssignedRoom = (tree, deviceId) => {
	const option = {};

	tree.forEach(region => {
		const hospitals = region.subOptions;
		hospitals.forEach(hospital => {
			const departments = hospital.subOptions;
			departments.forEach(department => {
				const floors = department.subOptions;
				floors.forEach(floor => {
					const rooms = floor.subOptions;
					rooms.forEach(room => {
						if (room.helloDeviceId === deviceId) {
							option.roomName = room.name;
							option.floorName = floor.name;
							option.departmentName = department.name;
							option.hospitalName = hospital.name;
							option.regionName = region.name;
						}
					});
				});
			});
		});
	});

	return option;
};

export const validateAge = dateOfBirth => {
	return moment().diff(dateOfBirth, 'years', false) <= 120;
};

export const getUserLocation = async (onSuccess, onError) => {
	navigator.geolocation.getCurrentPosition(onSuccess, onError, {
		enableHighAccuracy: true,
		timeout: 5000,
		maximumAge: 0,
	});
};

export const calcDistanceInMiles = (location1, location2) => {
	if (!location1 || !location2) {
		return null;
	}
	const radiusOfEarth = 3958.8; // Radius of the Earth in miles
	const rlat1 = location1.latitude * (Math.PI / 180); // Convert degrees to radians
	const rlat2 = location2.latitude * (Math.PI / 180); // Convert degrees to radians
	const difflat = rlat2 - rlat1; // Radian difference (latitudes)
	const difflng = (location2.longitude - location1.longitude) * (Math.PI / 180); // Radian difference (longitudes)
	const distance = (
		2 *
		radiusOfEarth *
		Math.asin(
			Math.sqrt(
				Math.sin(difflat / 2) * Math.sin(difflat / 2) +
					Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflng / 2) * Math.sin(difflng / 2)
			)
		)
	).toFixed(2);
	return parseFloat(distance);
};

export const searchSectors = (arr, name, sectors = []) => {
	// eslint-disable-next-line consistent-return
	arr.forEach(item => {
		if (!name) {
			// eslint-disable-next-line no-param-reassign
			item.hidden = false;
			// eslint-disable-next-line no-param-reassign
			item.matchesSearch = false;
		}
		if (item.name?.toLowerCase().includes(name.toLowerCase())) {
			// eslint-disable-next-line no-param-reassign
			item.hidden = false;
			// eslint-disable-next-line no-param-reassign
			item.matchesSearch = true;
			sectors.push(item);
		} else {
			// eslint-disable-next-line no-param-reassign
			item.hidden = true;
			// eslint-disable-next-line no-param-reassign
			item.matchesSearch = false;
		}
		if (item.subOptions && item.subOptions.length > 0) {
			return searchSectors(item.subOptions, name, sectors);
		}
	});
	return sectors;
};

export const showPath = (tree, arr) => {
	arr.forEach(item => {
		if (item.hospitalId) {
			const hospital = findSectorById(tree, item.hospitalId);
			hospital.hidden = false;
			hospital.matchesSearch = true;
		}

		if (item.departmentId) {
			const department = findSectorById(tree, item.departmentId);
			department.hidden = false;
			department.matchesSearch = true;
		}

		if (item.floorId) {
			const floor = findSectorById(tree, item.floorId);
			floor.hidden = false;
			floor.matchesSearch = true;
		}
	});
};

export const getStatus = statusId => {
	switch (statusId) {
		case AppointmentStatus.ACCEPTED:
			return 'accepted';
		case AppointmentStatus.PENDING:
			return 'pending';
		case AppointmentStatus.REJECTED:
			return 'rejected';
		case AppointmentStatus.CANCELED:
			return 'canceled';
		case AppointmentStatus.COMPLETED:
			return 'completed';
		default:
			return '';
	}
};

export const capitalizeLetterAndAddSpaces = (str = '') =>
	str.replace(/\b\w/g, c => c.toUpperCase()).replace(/([a-z])([A-Z])/g, '$1 $2');

export const getConfigurableRaiseAlert = () =>
	getStorage().getItem('configurableRaiseAlert') ? JSON.parse(getStorage().getItem('configurableRaiseAlert')) : false;

export const savePdf = async (ref, fileName, type, config, margin = [0.5, 0.5]) => {
	const charts = ref?.current;

	html2canvas(charts, config).then(canvas => {
		const image = { type, quality: 0.98 };

		const imgWidth = 8.5;
		let pageHeight = 11;

		const innerPageWidth = imgWidth - margin[0] * 2;
		const innerPageHeight = pageHeight - margin[1] * 2;

		// Calculate the number of pages.
		const pxFullHeight = canvas.height;
		const pxPageHeight = Math.floor(canvas.width * (pageHeight / imgWidth));
		const nPages = Math.ceil(pxFullHeight / pxPageHeight);

		// Define pageHeight separately so it can be trimmed on the final page.
		pageHeight = innerPageHeight;

		// Create a one-page canvas to split up the full image.
		const pageCanvas = document.createElement('canvas');
		const pageCtx = pageCanvas.getContext('2d');
		pageCanvas.width = canvas.width;
		pageCanvas.height = pxPageHeight;

		// Initialize the PDF.
		const pdf = new JsPDF('p', 'in', [8.5, 11]);

		for (let page = 0; page < nPages; page += 1) {
			// Trim the final page to reduce file size.
			if (page === nPages - 1 && pxFullHeight % pxPageHeight !== 0) {
				pageCanvas.height = pxFullHeight % pxPageHeight;
				pageHeight = (pageCanvas.height * innerPageWidth) / pageCanvas.width;
			}

			// Display the page.
			const w = pageCanvas.width;
			const h = pageCanvas.height;
			pageCtx.fillStyle = 'white';
			pageCtx.fillRect(0, 0, w, h);
			pageCtx.drawImage(canvas, 0, page * pxPageHeight, w, h, 0, 0, w, h);

			// Add the page to the PDF.
			if (page > 0) pdf.addPage();
			const imgData = pageCanvas.toDataURL(`image/${image.type}`, image.quality);
			pdf.addImage(imgData, image.type, margin[1], margin[0], innerPageWidth, pageHeight);
		}

		pdf.save(fileName);
	});
};

export const dataURLtoFile = (dataurl, filename) => {
	const arr = dataurl.split(',');
	const mime = arr[0].match(/:(.*?);/)[1];
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	/* eslint-disable no-plusplus */
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new File([u8arr], filename, { type: mime });
};

export const validateMaxLength = (event, maxLength) => {
	handleOnKeyDownNumeric(event);
	if (
		event.target.value.length === maxLength &&
		![
			KeyCodes.BACK_SPACE,
			KeyCodes.LETTER_A,
			KeyCodes.LEFT_ARROW,
			KeyCodes.UP_ARROW,
			KeyCodes.DOWN_ARROW,
			KeyCodes.RIGHT_ARROW,
			event.ctrlKey,
		].includes(event.which)
	) {
		event.preventDefault();
	}
};

export const downloadFileFromUrl = (imgLink, originalFileName) => {
	const link = document.createElement('a');
	link.setAttribute('href', imgLink);
	link.setAttribute('download', originalFileName);
	document.body.appendChild(link);
	link.click();
	link.remove();
};

export const getAppVersion = content => content.split('-')[0];

export const getCodeNameByOsVersion = osVersion => {
	const version = AndroidVersions.find(({ apiLevels }) => apiLevels.includes(+osVersion));
	return version?.codeName ?? 'N/A';
};

export const capitalizeEveryFirstLetter = str => {
	const arr = str.split(' ');
	arr.forEach((item, index) => {
		arr[index] = item.charAt(0).toUpperCase() + item.slice(1);
	});
	return arr.join(' ');
};

export const validateDecimalNumber = (event, max) => {
	handleOnKeyDownNumeric(event, true);
	if (
		(event.target.value > max || /^\d+\.\d\d$/.test(event.target.value)) &&
		![
			KeyCodes.BACK_SPACE,
			KeyCodes.LETTER_A,
			KeyCodes.LEFT_ARROW,
			KeyCodes.UP_ARROW,
			KeyCodes.DOWN_ARROW,
			KeyCodes.RIGHT_ARROW,
			event.ctrlKey,
		].includes(event.which)
	) {
		event.preventDefault();
	}
};

export const isDecimal = value => {
	return regexForDecimalNumbers.test(value);
};

export const getNursePoolingStatus = () => getStorage().getItem('nursePoolingStatus');

export const setDoctorSpecialty = specialty => {
	getStorage().setItem('doctorSpecialty', specialty);
};

export const getDoctorSpecialty = () => getStorage().getItem('doctorSpecialty');

export const createArrayWithDefaultValues = endIndex => {
	const newArray = [];
	for (let startIndex = 0; startIndex < endIndex; startIndex++) {
		newArray.push(startIndex);
	}
	return newArray;
};
export const setUserNotAuthorized = status => {
	getStorage().setItem('notAuthorized', status);
};

export const isStandAloneAppsUserAuthorized = () => getStorage().getItem('notAuthorized');

export const getLevelDevices = (option, includeRoom = false) => {
	const devices = [];
	switch (option.type) {
		case SectorTypes.HOSPITAL: {
			const departments = option.subOptions;
			departments.forEach(department => {
				const floors = department.subOptions;
				floors.forEach(floor => {
					const rooms = floor.subOptions;
					rooms.forEach(room => {
						if (room.helloDeviceId !== undefined) {
							devices.push(includeRoom ? room : room.helloDeviceId);
						}
					});
				});
			});
			return devices;
		}
		case SectorTypes.DEPARTMENT: {
			const floors = option.subOptions;
			floors.forEach(floor => {
				const rooms = floor.subOptions;
				rooms.forEach(room => {
					if (room.helloDeviceId) {
						devices.push(includeRoom ? room : room.helloDeviceId);
					}
				});
			});
			return devices;
		}
		case SectorTypes.FLOOR: {
			const rooms = option.subOptions;
			rooms.forEach(room => {
				if (room.helloDeviceId) {
					devices.push(includeRoom ? room : room.helloDeviceId);
				}
			});
			return devices;
		}
		default:
			return devices;
	}
};

export const isContinueAsSelected = () => getStorage().getItem('isContinueAs') != null;

export const getRoomTypeIdsToFilterWith = (rooms, userRole) => {
	let roomTypes = [];
	if (userRole === UserRoles.DOCTOR) {
		roomTypes = [RoomTypes.DOCTOR.type];
	}
	if (userRole === UserRoles.DIGITAL_CLINICIAN) {
		roomTypes = [RoomTypes.DIGITAL_CLINIC.type, RoomTypes.FRONT_DESK.type];
	}
	if (userRole === UserRoles.NURSE) {
		roomTypes = [
			RoomTypes.LABORATORY.type,
			RoomTypes.INFUSION_ROOM.type,
			RoomTypes.INJECTION_ROOM.type,
			RoomTypes.RADIOLOGY.type,
		];
	}
	return rooms.filter(item => roomTypes.includes(item.type));
};

export const downloadReturnReport = (reportRef, name) => {
	const fileName = name;
	const fileType = 'jpeg';
	const configurations = { useCORS: true, allowTaint: true, scrollY: 0 };
	savePdf(reportRef, fileName, fileType, configurations, [0.2, 0.2]);
};

export const getLevelIdByType = type => {
	let result;
	switch (type) {
		case SectorTypes.HOSPITAL: {
			result = DeviceListLevel.HOSPITAL;
			break;
		}
		case SectorTypes.DEPARTMENT: {
			result = DeviceListLevel.DEPARTMENT;
			break;
		}
		case SectorTypes.FLOOR: {
			result = DeviceListLevel.FLOOR;
			break;
		}
		case SectorTypes.ROOM: {
			result = DeviceListLevel.ROOM;
			break;
		}
		default: {
			result = null;
		}
	}
	return result;
};

export const getLevelDescriptionById = type => {
	switch (type) {
		case DeviceListLevel.HEALTH_SYSTEM: {
			return SectorTypes.HEALTH_SYSTEM;
		}
		case DeviceListLevel.HOSPITAL: {
			return SectorTypes.HOSPITAL;
		}
		case DeviceListLevel.DEPARTMENT: {
			return SectorTypes.DEPARTMENT;
		}
		case DeviceListLevel.FLOOR: {
			return SectorTypes.FLOOR;
		}
		case DeviceListLevel.ROOM: {
			return SectorTypes.ROOM;
		}
		default: {
			return SectorTypes.COMPANY;
		}
	}
};

export const getPort = url => {
	const parts = url.split(':'),
		port = parseInt(parts[parts.length - 1], 10);
	if (parts[0] === 'http' && (Number.isNaN(port) || parts.length < 3)) {
		return '';
	}
	if (parts[0] === 'https' && (Number.isNaN(port) || parts.length < 3)) {
		return '';
	}
	if (parts.length === 1 || Number.isNaN(port)) return '';
	return port;
};

export const getUserRoleId = userRole => {
	if (!userRole) {
		return null;
	}
	return CompanyRoleObjects.find(item => item.role.toLowerCase() === userRole?.toLowerCase())?.id;
};

export const getProperUrl = (configs, isPrimaryCare, role = getUserRole()) => {
	const adminMenu = getConfigurationMenu(configs.adminConfigurableMenu, role);
	const myMenu = getConfigurationMenu(configs.configurableMenu, role);
	const result = [];
	const isMyMenu = ![UserRoles.NURSE, UserRoles.DIGITAL_CLINICIAN, UserRoles.DOCTOR, UserRoles.VIRTUAL_SITTER].includes(role);
	if (isMyMenu) {
		Object.entries(myMenu).forEach(([, item]) => {
			if (item.value && !item.isGeneralSetting) {
				result.push(item);
			}
		});
	} else {
		Object.entries(adminMenu).forEach(([key, item]) => {
			if (myMenu[key].value && item.value && !item.isGeneralSetting) {
				result.push(item);
			}
		});
		if (result.filter(item => !item.isDefault).length === 0) {
			Object.entries(adminMenu).forEach(([, item]) => {
				if (item.value && !item.isGeneralSetting) {
					result.push(item);
				}
			});
		}
	}
	const nonChangableItem = getNonChangeableFirstItem(isPrimaryCare);
	if (nonChangableItem) {
		return nonChangableItem;
	}

	result.sort((a, b) => a.orderNo - b.orderNo);
	return result.length > 0 ? result[0].reDirectUrl : getDefaultRedirectLink(isPrimaryCare);
};

const getNonChangeableFirstItem = isPrimaryCare => {
	let result = null;
	switch (getUserRole()) {
		case UserRoles.NURSE: {
			result = isPrimaryCare ? 'nurse-home' : '';
			break;
		}
		case UserRoles.DOCTOR: {
			result = isPrimaryCare ? 'doctor-home' : '';
			break;
		}
		case UserRoles.DIGITAL_CLINICIAN: {
			result = '/check-in-dc';
			break;
		}
		default: {
			return null;
		}
	}
	return result;
};

export const getDefaultRedirectLink = isPrimaryCare => {
	const role = getUserRole();
	let url;
	switch (role) {
		case UserRoles.VIRTUAL_SITTER: {
			url = '/monitoring';
			break;
		}
		case UserRoles.SUPER_ADMIN: {
			url = '/companies.js';
			break;
		}
		case UserRoles.ADMIN:
		case UserRoles.SUPER_USER: {
			url = '/health-system';
			break;
		}
		case UserRoles.NURSE: {
			url = isPrimaryCare ? '/nurse-home' : '/health-system';
			break;
		}
		case UserRoles.PATIENT: {
			url = '/patient-home';
			break;
		}
		case UserRoles.DIGITAL_CLINICIAN: {
			url = '/check-in-dc';

			break;
		}
		case UserRoles.DOCTOR: {
			url = '/doctor-home';
			break;
		}
		case UserRoles.VISITOR: {
			url = '/devices';
			break;
		}
		default: {
			url = '';
		}
	}
	return url;
};

export const isCanvasEmpty = canvas => {
	const blank = document.createElement('canvas');
	blank.width = canvas.width;
	blank.height = canvas.height;

	return canvas.toDataURL() === blank.toDataURL();
};
export const isMobileOrTabletDevice = () =>
	'ontouchstart' in document.documentElement && 'orientation' in window && navigator.maxTouchPoints > 0;

export const isSmartLauncher = () => {
	return sessionStorage.getItem('ehr.sid') === 'smart';
};

export const setIsAdUser = value => {
	getStorage().setItem('isCompanyAdUser', value);
};

export const isAdUser = () => getStorage().getItem('isCompanyAdUser') === 'true';

export const setHasCompanyAdConfigurations = value => getStorage().setItem('hasCompanyAdConfigurations', value);

export const hasCompanyAdConfigurations = () => getStorage().getItem('hasCompanyAdConfigurations') === 'true';

export const getCallsButtonBackground = darkMode => {
	return darkMode ? DarkTheme.colors.grayFour : LightTheme.colors.grayZero;
};

export const getSomeConfigurationsValues = (configs, keys) => (configs ? keys.some(key => configs[key]?.value) : false);

export const getCallsButtonColor = darkMode => {
	return darkMode ? DarkTheme.colors.graySix : LightTheme.colors.grayFive;
};

export const getHealthSystemDevices = healthSystem => {
	const result = { busy: [], online: [], privacy: [], pairedRemote: [] };
	healthSystem.hospitals.forEach(hospital => {
		hospital.departments.forEach(department => {
			department.floors.forEach(floor => {
				floor.rooms.forEach(room => {
					if (room.callState === ParticipantState.CONNECTED.type) {
						result.busy.push(+room.helloDeviceId);
					}
					if (room.isOnline) {
						result.online.push(+room.helloDeviceId);
					}
					if (room.aiPrivacyStatus) {
						result.privacy.push(+room.helloDeviceId);
					}
					if (room.pairedRemote) {
						result.pairedRemote.push(+room.helloDeviceId);
					}
				});
			});
		});
	});
	return result;
};
export const getConfigurationMenu = (menuObject, userRole = getUserRole()) => menuObject[userRole] || {};

export const getConfigurationValue = config => (config ? config.value : false);

export const getConfigurationVariant = config => (config ? config.variant : '');

export const getRoleConfigurationValue = (configs, key) => (configs ? configs[key] : false);

export const getSomeRoleConfigurationsValues = (configs, keys) => (configs ? keys.some(key => configs[key]) : false);

export const getMonitoringConfigurationVariant = (config, key) =>
	config ? config?.variant : configurableMonitoringMenu(getUserRole())[key].variant;

export const updateConfigsList = (adminRoomSettings, myRoomSettings) => {
	const response = { configs: {}, error: null };
	const allMenus = {};

	if (adminRoomSettings.error) {
		response.error = adminRoomSettings.error.message;
	} else {
		adminRoomSettings.settings.forEach(item => {
			const config = configurableMonitoringMenu(getUserRole())[item.settingTypeId];
			if (config) {
				config.value = JSON.parse(item.value);
				config.isLocked = item.isLocked;
				if (item.variant) {
					config.variant = config.options.find(opt => opt.value === item.variant);
				}
				allMenus[item.settingTypeId] = config;
			}
		});
	}
	if (myRoomSettings.error) {
		response.error = myRoomSettings.error.message;
	} else {
		myRoomSettings.forEach(item => {
			if (allMenus[item.preferenceTypeId] && !allMenus[item.preferenceTypeId].isLocked) {
				allMenus[item.preferenceTypeId].value = JSON.parse(item.value);
			}
		});
	}

	response.configs = allMenus;
	return response;
};

export const clearStorageAndKeepCookiePrefrences = () => {
	const cookiePreferences = getStorage().getItem('cookiePreferences');
	const cookieAnswer = getStorage().getItem('cookieAnswer');
	getStorage().clear();
	getStorage().setItem('cookiePreferences', cookiePreferences);
	getStorage().setItem('cookieAnswer', cookieAnswer);
};

export const generateMRN = (length = 10) => {
	let result = '';
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	const charactersLength = characters.length;
	let counter = 0;
	while (counter < length) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
		counter += 1;
	}
	return result;
};

export const sortDevicesByTreeRoomsOrder = (arrayToSort, orderArray) =>
	orderArray.reduce((acc, obj) => {
		const foundItem = arrayToSort.find(item => item.roomId === obj.id);
		if (foundItem) {
			acc.push(foundItem);
		}
		return acc;
	}, []);

export const toPascalCase = string => {
	const str = string.trim();
	const words = str.split(/\s+|\W+/);
	const pascalCaseWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
	const pascalCaseString = pascalCaseWords.join('');
	return pascalCaseString;
};

export const reorderObjects = (originalObject, orderArray) => {
	const newObject = {};

	orderArray.forEach(key => {
		if (key in originalObject) {
			newObject[key] = originalObject[key];
		}
	});

	return newObject;
};

export const getLettersBasedIndex = index => {
	const alphabetLowercase = Array.from({ length: 26 }, (_, i) => String.fromCharCode(97 + i));
	if (index < 0 || index >= alphabetLowercase.length) {
		return null;
	}
	return alphabetLowercase[index];
};

export const downloadMultipleFiles = (files, fileName) => {
	for (let i = 0; i < files.length; i += 1) {
		const link = document.createElement('a');
		link.setAttribute('href', files[i].url);
		const urlSplitted = files[i].url.split('/');
		link.setAttribute('download', `${fileName} - ${urlSplitted.pop()}`);
		document.body.appendChild(link);
		link.click();
	}
};

export const snakeToCamel = str =>
	str.toLowerCase().replace(/([-_][a-z])/g, group => group.toUpperCase().replace('-', '').replace('_', ''));

export const getDescFromEnum = (type, enumObj) => {
	let result = '';
	for (let i = 0; i < Object.entries(enumObj).length; i++) {
		const [value, key] = Object.entries(enumObj)[i];
		if (key === type) {
			result = snakeToCamel(value);
			break;
		}
	}
	return result;
};
export const moveElement = (array, oldIndex, newIndex) => {
	if (oldIndex < 0 || oldIndex >= array.length || newIndex < 0 || newIndex >= array.length) {
		console.log('Invalid index values.');
		return array;
	}
	const element = array.splice(oldIndex, 1)[0];
	array.splice(newIndex, 0, element);
	return array;
};

export const isEhrContext = () => {
	const currentUrlParams = new URL(window.location.href).searchParams;
	if (currentUrlParams && currentUrlParams.get('email') && currentUrlParams.get('ts') && currentUrlParams.get('s')) {
		return true;
	}

	return false;
};

export const getIntegrationTypes = arr =>
	arr.map(settingTypeId => {
		let label, value;
		switch (settingTypeId) {
			case IntegrationTypesSettings.TELEHEALTH:
				label = 'Telehealth';
				value = TeamConfigurationIntegrationTypes.TELEHEALTH;
				break;
			case IntegrationTypesSettings.EVIDEON:
				label = 'eVideon';
				value = TeamConfigurationIntegrationTypes.EVIDEON;
				break;
			case IntegrationTypesSettings.PCARE:
				label = 'pCare';
				value = TeamConfigurationIntegrationTypes.PCARE;
				break;
			case IntegrationTypesSettings.GET_WELL:
				label = 'GetWell';
				value = TeamConfigurationIntegrationTypes.GET_WELL;
				break;
			case IntegrationTypesSettings.SONIFI:
				label = 'Sonifi';
				value = TeamConfigurationIntegrationTypes.SONIFI;
				break;
			default:
				label = '';
				value = '';
		}
		return { label, value };
	});

export const doesParamValueExist = property => property && property.value !== '0';

export const getRoleValueById = id => {
	switch (id) {
		case UserTypes.DOCTOR: {
			return translate('doctorOnList');
		}
		case UserTypes.NURSE: {
			return translate('nurseOnList');
		}
		case UserTypes.PATIENT: {
			return translate('patientOnList');
		}
		case UserTypes.ADMIN: {
			return translate('adminOnList');
		}
		case UserTypes.SUPER_USER: {
			return translate('superUserOnList');
		}
		case UserTypes.SUPER_ADMIN: {
			return translate('superAdminOnList');
		}
		case UserTypes.OWNER: {
			return translate('ownerOnList');
		}
		case UserTypes.DIGITAL_CLINICIAN: {
			return translate('digitalClinicianOnList');
		}
		case UserTypes.FAMILY_MEMBER: {
			return translate('familyMember');
		}
		case UserTypes.VIRTUAL_SITTER: {
			return translate('virtualSitterOnList');
		}
		case UserTypes.VISITOR: {
			return translate('visitor');
		}
		default:
			return '';
	}
};

export const getUserTypeBasedOnCompanyRole = companyRole => {
	switch (companyRole) {
		case CompanyRoles.DOCTOR:
			return UserTypes.DOCTOR;
		case CompanyRoles.VIRTUAL_CARE_PROVIDER:
			return UserTypes.NURSE;
		case CompanyRoles.VIRTUAL_SITTER:
			return UserTypes.VIRTUAL_SITTER;
		case CompanyRoles.PATIENT:
		case CompanyRoles.MEMBER:
			return UserTypes.PATIENT;
		case CompanyRoles.ADMIN:
			return UserTypes.ADMIN;
		case CompanyRoles.GUEST:
			return UserTypes.GUEST;
		case CompanyRoles.SUPER_USER:
			return UserTypes.SUPER_USER;
		case CompanyRoles.SUPER_ADMIN:
			return UserTypes.SUPER_ADMIN;
		case CompanyRoles.DIGITAL_CLINICIAN:
			return UserTypes.DIGITAL_CLINICIAN;
		case CompanyRoles.VISITOR:
			return UserTypes.VISITOR;
		case CompanyRoles.OWNER:
			return UserTypes.OWNER;
		default:
			return null;
	}
};

export const getCompanyRoleBasedOnUserType = role => {
	switch (role) {
		case UserTypes.NURSE:
			return CompanyRoles.VIRTUAL_CARE_PROVIDER;
		case UserTypes.ADMIN:
			return CompanyRoles.ADMIN;
		case UserTypes.SUPER_ADMIN:
			return CompanyRoles.SUPER_ADMIN;
		case UserTypes.SUPER_USER:
			return CompanyRoles.SUPER_USER;
		case UserTypes.DIGITAL_CLINICIAN:
			return CompanyRoles.DIGITAL_CLINICIAN;
		case UserTypes.DOCTOR:
			return CompanyRoles.DOCTOR;
		case UserTypes.VIRTUAL_SITTER:
			return CompanyRoles.VIRTUAL_SITTER;
		default:
			break;
	}
	return null;
};

export const isRedirectVirtualCareSession = () => {
	const redirectUri = getStorage().getItem('redirectUri');
	if (redirectUri) {
		return redirectUri.startsWith('/virtual-care-session/');
	}
	return false;
};

export const getAge = dateOfBirth => moment.utc().diff(moment.utc(dateOfBirth), 'years');

export const getRequestType = requestType => {
	switch (requestType) {
		case RequestType.RAPID:
			return CallRequestType.RAPID_REQUEST_TYPE;
		case RequestType.ADMISSION:
			return CallRequestType.ADMISSION_REQUEST_TYPE;
		default:
			return CallRequestType.REGULAR_REQUEST_TYPE;
	}
};

export const getRequestColor = requestType => {
	switch (requestType) {
		case RequestType.RAPID:
			return 'var(--red-1)';
		case RequestType.ADMISSION:
			return 'var(--orange-3)';
		default:
			return 'var(--blue-2)';
	}
};

export const getBase64 = file => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = readerEvent => {
			const image = new Image();
			image.onload = () => {
				// Resize the image
				const maxSize = 1080;
				const canvas = document.createElement('canvas');
				let { width, height } = image;
				if (width > height) {
					if (width > maxSize) {
						height *= maxSize / width;
						width = maxSize;
					}
				} else if (height > maxSize) {
					width *= maxSize / height;
					height = maxSize;
				}
				canvas.width = width;
				canvas.height = height;
				canvas.getContext('2d').drawImage(image, 0, 0, width, height);
				const dataUrl = canvas.toDataURL('image/jpeg');
				resolve(dataUrl);
			};
			// @ts-ignore
			image.src = readerEvent.target.result;
		};
		reader.onerror = error => {
			// eslint-disable-next-line no-console
			console.log(error);
			reject();
		};
	});
};

export const setUserBackground = async ({
	localParticipant,
	userSettings = {},
	visualSettings = {},
	getBlobSasPicture,
	healthSystemId,
}) => {
	if (UserRoles.GUEST === getUserRole()) {
		return;
	}
	const callBackground = userSettings[UserSettings.CALL_BACKGROUND];
	const backgroundConfig = getConfig(visualSettings[CompanySettings.DIGITAL_BACKGROUND]);
	const blurryBackgroundConfig = getConfig(visualSettings[CompanySettings.BLURRY_BACKGROUND]);

	if (callBackground === VirtualBackgroundTypes.BLUR) {
		const params = { localParticipant, streamEffect: null, selectedBackground: VirtualBackgroundTypes.BLUR };
		changeLocalParticipantBackground(params);
		return;
	}

	if (callBackground === VirtualBackgroundTypes.NONE) {
		const params = { localParticipant, streamEffect: null, selectedBackground: VirtualBackgroundTypes.NONE };
		changeLocalParticipantBackground(params);
		return;
	}

	if (!callBackground && blurryBackgroundConfig.value) {
		const params = { localParticipant, streamEffect: null, selectedBackground: VirtualBackgroundTypes.BLUR };
		changeLocalParticipantBackground(params);
	}

	if (!backgroundConfig.value) {
		const params = { localParticipant, streamEffect: null, selectedBackground: VirtualBackgroundTypes.NONE };
		changeLocalParticipantBackground(params);
		return;
	}

	if (![VirtualBackgroundTypes.NONE, VirtualBackgroundTypes.BLUR].includes(callBackground)) {
		const response = await getBackgroundImages(getCompanyId(), healthSystemId);
		if (!response.error && response.length > 0 && response.find(item => item.pictureUrl === callBackground)) {
			const blobResponse = await getBlobSasPicture(callBackground, 'team-call-background-pictures');
			if (!blobResponse.error) {
				const params = {
					localParticipant,
					streamEffect: null,
					selectedBackground: null,
					backgroundUrl: blobResponse.result.uri.uri,
				};
				changeLocalParticipantBackground(params);
				return;
			}
		}
	}
};

export const isJSON = text => {
	if (typeof text !== 'string') {
		return false;
	}
	try {
		JSON.parse(text);
		return true;
	} catch (error) {
		return false;
	}
};

export const getAudioDuration = (link, callback) => {
	const audio = new Audio(link);
	audio.addEventListener('loadedmetadata', () => {
		callback(audio.duration);
	});
	audio.load();
};

export const buildParamsFromArray = (paramName, array, isFirstParam) => {
	if (array === null) {
		return `${paramName}=null`;
	}

	if (array.length === 0) {
		return ``;
	}

	return array.reduce((acc, item, index) => {
		const separator = isFirstParam && index === 0 ? '' : '&';

		return `${acc}${separator}${paramName}=${item}`;
	}, '');
};

export const buildQueryString = obj => {
	const nonFalsyProperties = Object.fromEntries(Object.entries(obj).filter(([key, value]) => value));

	const queryString = Object.entries(nonFalsyProperties)
		.map(([key, value]) => `${encodeURIComponent(_.kebabCase(key))}=${encodeURIComponent(value)}`)
		.join('&');

	return queryString;
};

export const getIsOldUserExperience = (companyConfigurations, hsSettings) => {
	const isOldExperienceCompany = getConfigurationValue(companyConfigurations[GeneralAndMenuSettings.IS_OLD_USER_EXPERIENCE]);
	const isOldExperienceHs =
		hsSettings.find(item => item.settingTypeId === GeneralAndMenuSettings.IS_OLD_USER_EXPERIENCE)?.value === 'true';
	const isExperienceHsChangeable = getConfigurationValue(
		companyConfigurations[GeneralAndMenuSettings.IS_EXPERIENCE_HS_CHANGEABLE]
	);
	return isExperienceHsChangeable && isOldExperienceCompany ? isOldExperienceHs : isOldExperienceCompany;
};

export const getAiSettingsConfigurations = (adminRoomSettings, nurseRoomSettings) => {
	const response = { configs: {}, error: null };
	const allMenus = {};

	if (adminRoomSettings.error) {
		response.error = adminRoomSettings.error.message;
	} else {
		adminRoomSettings.settings.forEach(item => {
			const config = configurableAISettings()[item.settingTypeId];
			if (config) {
				config.value = JSON.parse(item.value);
				config.isLocked = item.isLocked;
				if (item.variant) {
					config.variant = config.options.find(opt => opt.value === item.variant);
				}
				allMenus[item.settingTypeId] = config;
			}
		});
	}
	if (nurseRoomSettings.error) {
		response.error = nurseRoomSettings.error.message;
	} else {
		nurseRoomSettings.forEach(item => {
			if (allMenus[item.preferenceTypeId] && !allMenus[item.preferenceTypeId].isLocked) {
				allMenus[item.preferenceTypeId].value = JSON.parse(item.value);
			}
		});
	}

	response.configs = allMenus;
	return response;
};